// traps Tab navigation inside a container, preventing focus from escaping
export function trapFocus(
  navigationRef?: HTMLDivElement | null,
  triggerElement?: HTMLElement | null,
): () => void {
  if (!navigationRef || !triggerElement) return () => {};

  const focusableEls = getKeyboardFocusableElements(navigationRef);

  // Focus first element on next paint frame
  requestAnimationFrame(() => {
    focusableEls[0]?.focus();
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const currentFocusableEls = getKeyboardFocusableElements(navigationRef);
    const activeEl = document.activeElement as HTMLElement;

    if (!currentFocusableEls.length) {
      e.preventDefault();
      triggerElement.focus();
      return;
    }

    const firstEl = currentFocusableEls[0];
    const lastEl = currentFocusableEls[currentFocusableEls.length - 1];

    if (!navigationRef.contains(activeEl)) {
      e.preventDefault();
      triggerElement.focus();
      return;
    }

    if (e.shiftKey) {
      if (activeEl === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
    } else if (activeEl === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };

  navigationRef.addEventListener('keydown', handleKeyDown);

  return () => {
    navigationRef.removeEventListener('keydown', handleKeyDown);
  };
}

export function getKeyboardFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ];

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter(
    (el) => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    },
  );
}

// moves focus between elements when a specific key is pressed (arrow navigation)
export function navigate(
  container: HTMLElement,
  key: string,
  forwardKey: string,
  backwardKey: string,
) {
  const items = Array.from(container.querySelectorAll<HTMLElement>('a, button')).filter(
    (el) => el.offsetParent !== null,
  );

  const active = document.activeElement as HTMLElement;
  const index = items.indexOf(active);
  if (index === -1) return;

  if (key === forwardKey) {
    const next = (index + 1) % items.length;
    items[next].focus();
  } else if (key === backwardKey) {
    const prev = (index - 1 + items.length) % items.length;
    items[prev].focus();
  }
}

export const trapFocusWithDelay = (
  element: HTMLDivElement,
  triggerButton: HTMLElement,
  delay: number,
) => {
  setTimeout(() => {
    if (element && triggerButton) {
      trapFocus(element, triggerButton);
    }
  }, delay);
};

export const handleSubmenuFocus = (
  submenuRef: React.RefObject<HTMLDivElement>,
  triggerButtonRef: React.RefObject<HTMLButtonElement>,
  showSubmenu: boolean,
  openSubmenu: () => void,
  focusDelay: number,
) => {
  if (submenuRef.current && triggerButtonRef.current) {
    if (!showSubmenu) {
      openSubmenu();
    }
    trapFocusWithDelay(submenuRef.current, triggerButtonRef.current, focusDelay);
  }
};

export function attachVerticalNavigation(container: HTMLElement) {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      navigate(container, e.key, 'ArrowDown', 'ArrowUp');
    }
  };
  container.addEventListener('keydown', handler);
  return () => {
    container.removeEventListener('keydown', handler);
  };
}

export function attachHorizontalNavigation(container: HTMLElement) {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      navigate(container, e.key, 'ArrowRight', 'ArrowLeft');
    }
  };
  container.addEventListener('keydown', handler);
  return () => {
    container.removeEventListener('keydown', handler);
  };
}
